/* @jsx h */
import { computed, defineComponent, h, onMounted, ref } from "vue";
import { Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import { Avatar } from "../shared/Avatar";
import { LinkModal } from "./LinkModal";
import { MemberEditor } from "./MemberEditor";
import { RolesModal } from "./RolesModal";
import { RulesModal } from "./RulesModal";
import type { Member, Role, RotaSettings } from "./types";
import { SubpageHeader } from "@app/vue/components/SubpageHeader";

// The people on the rota, what they can do, and their personal links.
// See docs/plans/workday/staff_planning.md (phase 1a).
export const WorkDayTeamPage = defineComponent({
  name: "WorkDayTeamPage",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();

    const loading = ref(true);
    const members = ref<Member[]>([]);
    const roles = ref<Role[]>([]);
    const settings = ref<RotaSettings | null>(null);
    const canSeeWages = ref(false);
    const editing = ref<Member | null | undefined>(undefined);
    const rolesOpen = ref(false);
    const rulesOpen = ref(false);
    const link = ref<{ member: Member; url: string; emailed: boolean } | null>(
      null,
    );
    const sending = ref<number | null>(null);
    const showInactive = ref(false);

    const load = async () => {
      try {
        const [team, r, s] = await Promise.all([
          trpc.rota.team.list.query(),
          trpc.rota.roles.list.query(),
          trpc.rota.settings.get.query(),
        ]);
        members.value = team.members as Member[];
        canSeeWages.value = team.can_see_wages;
        roles.value = r as Role[];
        settings.value = s as RotaSettings;
      } catch {
        toast.showToast(t("workday.common.load_failed"), { type: "error" });
      } finally {
        loading.value = false;
      }
    };
    onMounted(load);

    const active = computed(() => members.value.filter((m) => m.active));
    const inactive = computed(() => members.value.filter((m) => !m.active));
    const roleName = (id: number) => roles.value.find((r) => r.id === id)?.name;

    const sendLink = async (m: Member) => {
      if (m.has_link && !confirm(t("workday.team.resend_confirm"))) return;
      sending.value = m.id;
      try {
        const res = await trpc.rota.team.sendLink.mutate({ id: m.id });
        link.value = { member: m, url: res.url, emailed: res.emailed };
        await load();
      } catch (e) {
        toast.showToast(
          (e as { message?: string })?.message ??
            t("workday.common.save_failed"),
          { type: "error" },
        );
      } finally {
        sending.value = null;
      }
    };

    const remove = async (m: Member) => {
      if (!confirm(t("workday.team.delete_confirm").replace("{name}", m.name)))
        return;
      await trpc.rota.team.delete.mutate({ id: m.id });
      await load();
    };

    const card = (m: Member) => (
      <div
        key={m.id}
        class={[
          "flex flex-col rounded-xl border border-base-300 bg-base-100 p-4 shadow-sm",
          m.active ? "" : "opacity-60",
        ].join(" ")}
      >
        <div class="flex items-start gap-3">
          <Avatar name={m.name} id={m.id} color={m.color} size="md" />
          <div class="min-w-0 flex-1">
            <div class="truncate font-semibold">{m.name}</div>
            <div class="truncate text-xs text-base-content/60">
              {m.email ?? t("workday.team.no_email")}
            </div>
          </div>
          <div class="dropdown dropdown-end">
            <div
              tabindex={0}
              role="button"
              class="btn btn-ghost btn-sm btn-square"
              aria-label={t("workday.common.actions")}
            >
              <span class="material-symbols-outlined">more_vert</span>
            </div>
            <ul
              tabindex={0}
              class="menu dropdown-content z-20 w-48 rounded-box border border-base-300 bg-base-100 p-1 shadow-lg"
            >
              <li>
                <a onClick={() => (editing.value = m)}>
                  <span class="material-symbols-outlined text-base">edit</span>
                  {t("workday.common.edit")}
                </a>
              </li>
              <li>
                <a onClick={() => sendLink(m)}>
                  <span class="material-symbols-outlined text-base">link</span>
                  {m.has_link
                    ? t("workday.team.new_link")
                    : t("workday.team.send_link")}
                </a>
              </li>
              <li>
                <a class="text-error" onClick={() => remove(m)}>
                  <span class="material-symbols-outlined text-base">
                    delete
                  </span>
                  {t("workday.common.delete")}
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div class="mt-3 flex min-h-6 flex-wrap gap-1">
          {m.role_ids.length ? (
            m.role_ids.map((id) => (
              <span key={id} class="badge badge-ghost badge-sm">
                {roleName(id)}
              </span>
            ))
          ) : (
            <span class="text-xs text-base-content/40">
              {t("workday.team.no_competences")}
            </span>
          )}
        </div>
        <div class="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-base-content/60">
          {m.contract_hours_week != null && (
            <span class="inline-flex items-center gap-1">
              <span class="material-symbols-outlined text-sm">schedule</span>
              {t("workday.team.hours_week_value").replace(
                "{hours}",
                String(m.contract_hours_week),
              )}
            </span>
          )}
          {canSeeWages.value && m.hourly_rate != null && (
            <span class="inline-flex items-center gap-1">
              <span class="material-symbols-outlined text-sm">payments</span>
              {t("workday.team.rate_value").replace(
                "{rate}",
                m.hourly_rate.toFixed(2),
              )}
            </span>
          )}
          {m.user_name && (
            <span
              class="inline-flex items-center gap-1"
              title={t("workday.team.pos_user")}
            >
              <span class="material-symbols-outlined text-sm">
                point_of_sale
              </span>
              {m.user_name}
            </span>
          )}
        </div>
        <div class="mt-auto flex items-center justify-between gap-2 border-t border-base-300 pt-3 mt-4">
          <span class="text-xs text-base-content/50">
            {m.link_sent_at
              ? t("workday.team.link_sent_on").replace(
                  "{date}",
                  new Date(m.link_sent_at).toLocaleDateString(),
                )
              : t("workday.team.no_link")}
          </span>
          <button
            class={[
              "btn btn-xs",
              m.has_link ? "btn-ghost" : "btn-primary",
            ].join(" ")}
            disabled={sending.value === m.id}
            onClick={() => sendLink(m)}
          >
            <span class="material-symbols-outlined text-sm">
              {m.has_link ? "refresh" : "send"}
            </span>
            {m.has_link
              ? t("workday.team.new_link")
              : t("workday.team.send_link")}
          </button>
        </div>
      </div>
    );

    return () => (
      <div class="flex h-full w-full flex-col overflow-hidden bg-base-200">
        <SubpageHeader
          title={t("workday.nav.team")}
          subtitle={t("workday.team.subtitle")}
        >
          <div class="flex flex-wrap gap-2">
            <button
              class="btn btn-ghost btn-sm border-base-300"
              onClick={() => (rolesOpen.value = true)}
            >
              <span class="material-symbols-outlined text-base">badge</span>
              {t("workday.team.roles")}
            </button>
            <button
              class="btn btn-ghost btn-sm border-base-300"
              disabled={!settings.value}
              onClick={() => (rulesOpen.value = true)}
            >
              <span class="material-symbols-outlined text-base">gavel</span>
              {t("workday.team.rules")}
            </button>
            <button
              class="btn btn-primary btn-sm"
              onClick={() => (editing.value = null)}
            >
              <span class="material-symbols-outlined text-base">
                person_add
              </span>
              {t("workday.team.add")}
            </button>
          </div>
        </SubpageHeader>
        <div class="flex-1 space-y-6 overflow-auto p-5">
          {!loading.value && roles.value.length === 0 && (
            <div class="alert alert-info text-sm">
              <span class="material-symbols-outlined">badge</span>
              <span>{t("workday.team.roles_first")}</span>
              <button
                class="btn btn-sm"
                onClick={() => (rolesOpen.value = true)}
              >
                {t("workday.team.roles")}
              </button>
            </div>
          )}

          {loading.value ? (
            <div class="flex justify-center p-10">
              <Loading />
            </div>
          ) : members.value.length === 0 ? (
            <div class="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-base-300 bg-base-100 px-6 py-16 text-center">
              <span class="material-symbols-outlined text-5xl text-primary">
                groups
              </span>
              <h2 class="text-lg font-semibold">
                {t("workday.team.empty_title")}
              </h2>
              <p class="max-w-md text-sm text-base-content/60">
                {t("workday.team.empty_help")}
              </p>
              <button
                class="btn btn-primary btn-sm"
                onClick={() => (editing.value = null)}
              >
                {t("workday.team.add")}
              </button>
            </div>
          ) : (
            <>
              <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {active.value.map(card)}
              </div>
              {inactive.value.length > 0 && (
                <div>
                  <button
                    class="btn btn-ghost btn-sm"
                    onClick={() => (showInactive.value = !showInactive.value)}
                  >
                    <span class="material-symbols-outlined text-base">
                      {showInactive.value ? "expand_less" : "expand_more"}
                    </span>
                    {t("workday.team.inactive").replace(
                      "{count}",
                      String(inactive.value.length),
                    )}
                  </button>
                  {showInactive.value && (
                    <div class="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                      {inactive.value.map(card)}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {editing.value !== undefined && (
          <MemberEditor
            member={editing.value}
            roles={roles.value}
            canSeeWages={canSeeWages.value}
            onClose={async (changed: boolean) => {
              editing.value = undefined;
              if (changed) await load();
            }}
          />
        )}
        {rolesOpen.value && (
          <RolesModal
            roles={roles.value}
            onClose={async (changed: boolean) => {
              rolesOpen.value = false;
              if (changed) await load();
            }}
          />
        )}
        {rulesOpen.value && settings.value && (
          <RulesModal
            settings={settings.value}
            onClose={async (changed: boolean) => {
              rulesOpen.value = false;
              if (changed) await load();
            }}
          />
        )}
        {link.value && (
          <LinkModal
            member={link.value.member}
            url={link.value.url}
            emailed={link.value.emailed}
            onClose={() => (link.value = null)}
          />
        )}
      </div>
    );
  },
});
